/* eslint-disable */
import React, { useState, useMemo } from 'react';
import PhoneScannerScreen from '../PhoneScannerScreen';
import RolePicker from './RolePicker';

/**
 * ScanOutSheet — scan a device QR, pick role/operative, create allocation.
 */

const stripSerial=(s)=>{const m=s.match(/\/sn\/([^/?#]+)/);if(m)return m[1].trim();const m2=s.match(/sn([A-Za-z0-9]+)$/i);if(m2)return m2[1].trim();if(s.startsWith("http")){const p=s.replace(/\/$/,"").split("/");return p[p.length-1].trim();}return s;};
const generateAnonLabel = () => 'DEV-' + Math.random().toString(36).slice(2, 6).toUpperCase();

export default function ScanOutSheet({ st, setSt, shiftId, account, audit, onBack, onSuccess }) {
  const [scannedDevice, setScannedDevice] = useState(null);
  const [error, setError] = useState(null);
  const [manualSerial, setManualSerial] = useState('');
  const [role, setRole] = useState('');
  const [operativeName, setOperativeName] = useState('');
  const [operativePhone, setOperativePhone] = useState('');
  const [anonLabel] = useState(() => generateAnonLabel());
  const [notes, setNotes] = useState('');

  const shift = (st.shifts || []).find(s => s.id === shiftId);
  const isAnonymous = shift?.anonymousMode === true;
  const assignmentRoles = st.assignmentRoles || [];

  // Recent operatives on this shift for quick-pick
  const recentOps = useMemo(() => {
    const names = new Map();
    (st.allocations || []).filter(a => a.shiftId === shiftId && a.operativeName).forEach(a => {
      if (!names.has(a.operativeName)) names.set(a.operativeName, a.operativePhone || '');
    });
    return Array.from(names.entries()).slice(0, 6); // [[name, phone], ...]
  }, [st.allocations, shiftId]);

  const handleScan = (code) => {
    const serial = stripSerial(code);
    const device = (st.devices || []).find(d =>
      d.serial === serial || d.serial === code || d.id === serial ||
      (d.serial && d.serial.toLowerCase().includes(serial.toLowerCase()))
    );

    if (!device) {
      setError(`Device not found: ${serial}`);
      setScannedDevice(null);
      return;
    }

    // Check if already out on this shift
    const existingAlloc = (st.allocations || []).find(a => a.deviceId === device.id && a.shiftId === shiftId && a.status === 'out');
    if (existingAlloc) {
      setError(`Device ${serial} is already out on this shift (${existingAlloc.role} · ${existingAlloc.operativeName || 'unassigned'}). Did you mean scan in?`);
      setScannedDevice(null);
      return;
    }

    // Check if device is assigned to a different location
    const shiftLoc = shift?.locationId;
    if (device.assignedLocationId && device.assignedLocationId !== shiftLoc && device.location !== (st.locations || []).find(l => l.id === shiftLoc)?.name) {
      const otherLoc = (st.locations || []).find(l => l.id === device.assignedLocationId || l.name === device.location);
      setError(`This device is assigned to ${otherLoc?.name || device.location || 'another location'} — cannot scan out here.`);
      setScannedDevice(null);
      return;
    }

    setError(null);
    setScannedDevice(device);
  };

  const handleConfirm = () => {
    if (!scannedDevice || !role) return;

    const now = new Date().toISOString();
    const allocId = 'alloc-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);

    setSt(prev => ({
      ...prev,
      allocations: [...(prev.allocations || []), {
        id: allocId,
        shiftId,
        deviceId: scannedDevice.id,
        role,
        operativeName: operativeName || null,
        operativePhone: operativePhone || null,
        outAt: now,
        inAt: null,
        status: 'out',
        notes: notes || null,
        anonDeviceLabel: isAnonymous ? anonLabel : null,
        swappedFromAllocationId: null,
        swappedToAllocationId: null,
        carriedFromAllocationId: null,
        flaggedByUserId: null,
        flaggedAt: null,
        recoveredAt: null,
        createdByUserId: account?.username || 'unknown',
        createdAt: now,
      }],
      devices: prev.devices.map(d => d.id === scannedDevice.id ? { ...d, availability: 'deployed' } : d),
    }));

    audit('allocation_created', { shiftId, deviceId: scannedDevice.id, role, operative: operativeName });

    if (onSuccess) onSuccess({ device: scannedDevice, role, operativeName });
  };

  const loc = (st.locations || []).find(l => l.id === shift?.locationId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Header */}
      <div style={{ padding: '8px 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface-app)', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: '50%', color: 'var(--text-muted)', cursor: 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-default)' }}>Scan device out</div>
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>Shift {shift?.shiftNumber} · {shift?.possessionRef}</div>
        </div>
        <div style={{ width: 36 }} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {/* Device lookup — manual input + optional camera */}
        {!scannedDevice && !error && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                autoFocus
                value={manualSerial}
                onChange={e => setManualSerial(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && manualSerial.trim()) handleScan(manualSerial.trim()); }}
                placeholder="Enter serial number or scan QR..."
                style={{ flex: 1, padding: '12px 14px', background: 'var(--surface-inset)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--text-default)', fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box' }}
              />
              <button
                onClick={() => manualSerial.trim() && handleScan(manualSerial.trim())}
                disabled={!manualSerial.trim()}
                style={{ padding: '12px 18px', borderRadius: 10, border: 'none', background: manualSerial.trim() ? '#ff530d' : 'var(--surface-raised)', color: manualSerial.trim() ? '#fff' : 'var(--text-subtle)', fontWeight: 600, fontSize: 13, cursor: manualSerial.trim() ? 'pointer' : 'not-allowed', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
              >
                Look up
              </button>
            </div>
            <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-subtle)', marginBottom: 10 }}>or scan with camera</div>
            <div style={{ borderRadius: 14, overflow: 'hidden', background: '#111', border: '1px solid var(--border-default)' }}>
              <PhoneScannerScreen onScan={handleScan} compact />
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid #f87171', borderRadius: 10, padding: '12px 14px', fontSize: 12, color: '#f87171', marginBottom: 16 }}>
            <strong style={{ display: 'block', color: 'var(--text-default)', marginBottom: 2 }}>Scan issue</strong>
            {error}
            <button onClick={() => { setError(null); setScannedDevice(null); setManualSerial(''); }} style={{ marginTop: 8, display: 'block', background: 'transparent', border: '1px solid #f87171', borderRadius: 6, color: '#f87171', padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Try again</button>
          </div>
        )}

        {/* Scanned device card */}
        {scannedDevice && (
          <>
            <div style={{ background: 'rgba(255,83,13,0.15)', border: '1px solid rgba(255,83,13,0.35)', borderRadius: 14, padding: 14, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#ff530d', fontVariantNumeric: 'tabular-nums', fontFamily: isAnonymous ? 'monospace' : 'inherit' }}>{isAnonymous ? anonLabel : scannedDevice.serial}</span>
                <span style={{ fontSize: 11, color: 'var(--text-default)', padding: '3px 8px', background: 'rgba(0,0,0,0.3)', borderRadius: 4, fontWeight: 500 }}>{scannedDevice.type}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 4 }}>
                <span style={{ color: 'var(--text-muted)' }}>Location</span>
                <span style={{ color: 'var(--text-default)', fontWeight: 500 }}>{loc?.name || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 4 }}>
                <span style={{ color: 'var(--text-muted)' }}>Status</span>
                <span style={{ color: '#4ade80', fontWeight: 500 }}>{scannedDevice.availability || 'Available'}</span>
              </div>
            </div>

            {/* Role picker */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Assignment role</label>
              <RolePicker roles={assignmentRoles} value={role} onChange={setRole} />
            </div>

            {/* Operative quick-picks */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                Operative name <span style={{ color: 'var(--text-subtle)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional — leave blank for plant)</span>
              </label>
              {recentOps.length > 0 && (
                <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 10, paddingBottom: 4 }}>
                  {recentOps.map(([name, phone]) => (
                    <button key={name} type="button" onClick={() => { setOperativeName(name); if (phone) setOperativePhone(phone); }}
                      style={{ flexShrink: 0, padding: '6px 10px', background: 'var(--surface-inset)', border: '1px solid var(--border-default)', borderRadius: 999, color: 'var(--text-default)', fontFamily: 'inherit', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {name}
                    </button>
                  ))}
                </div>
              )}
              <input value={operativeName} onChange={e => setOperativeName(e.target.value)} placeholder="Name" style={{ width: '100%', padding: '10px 12px', background: 'var(--surface-inset)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--text-default)', fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box' }} />
            </div>

            {/* Phone */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                Phone <span style={{ color: 'var(--text-subtle)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
              </label>
              <input value={operativePhone} onChange={e => setOperativePhone(e.target.value)} placeholder="07xxx xxxxxx" type="tel" style={{ width: '100%', padding: '10px 12px', background: 'var(--surface-inset)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--text-default)', fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box' }} />
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                Notes <span style={{ color: 'var(--text-subtle)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
              </label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any issues or handover notes" style={{ width: '100%', padding: '10px 12px', background: 'var(--surface-inset)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--text-default)', fontFamily: 'inherit', fontSize: 14, resize: 'none', minHeight: 60, boxSizing: 'border-box' }} />
            </div>

            <button onClick={handleConfirm} disabled={!role} style={{ width: '100%', padding: 13, borderRadius: 10, border: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: role ? 'pointer' : 'not-allowed', background: role ? '#ff530d' : 'var(--surface-raised)', color: role ? '#fff' : 'var(--text-subtle)' }}>
              Confirm allocation · Out now
            </button>
          </>
        )}
      </div>
    </div>
  );
}
